/**
 * Pakete-Widget P1 (docs/KONZEPT-PAKETE-WIDGET.md, Mockup „Pakete als Widget“, freigegeben
 * 04.10.2026): Karte „Auf der Website“ und Vorschau „So erscheint es auf der Website“ auf der
 * Seite „Paket bearbeiten“.
 *
 * Die Regel, ab wann ein Paket auf die Website darf (EN/IT/FR gepflegt, mindestens eine aktive
 * Kategorie), kommt aus `@maestro/pricing` — dieselbe, die die API serverseitig prueft. Der
 * Preistext kommt ebenfalls von dort; die Karte rechnet nichts.
 */
import { useState } from "react";
import {
  fehlendeWebsiteSprachen,
  paketWebsitePreisText,
  type PaketUebersetzungen,
} from "@maestro/pricing";
import { Button, Card, Icon, Input, Select, Textarea, Toggle } from "./components";

export type WebsiteSprache = "en" | "it" | "fr";
const SPRACHEN: WebsiteSprache[] = ["en", "it", "fr"];
const SPRACH_NAME: Record<"de" | WebsiteSprache, string> = {
  de: "Deutsch",
  en: "English",
  it: "Italiano",
  fr: "Français",
};

export interface WebsiteKategorie {
  id: string;
  name: string;
  archivedAt?: string | null;
}

/** Was die Karte vom Paket braucht. */
export interface WebsitePaket {
  id?: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  priceFlatCents?: number;
  minGuests?: number | null;
  websiteVisible?: boolean;
  translations?: PaketUebersetzungen | null;
  categoryIds?: string[];
  /** Migration 750: optionale deutsche „Art“ der Website-Karte; leer = automatisch aus den Gängen. EN/IT/FR: translations.<lang>.art. */
  websiteArt?: string | null;
  /** Migration 750: Paket mit Weinbegleitung zu diesem Menü; leer = Namensregel („… mit Weinbegleitung“). */
  winePairingPackageId?: string | null;
}

/** Andere Pakete, die als Weinbegleitung eines Menüs wählbar sind. */
export interface WeinKandidat {
  id: string;
  name: string;
}

/** Warum das Paket (noch) nicht auf die Website darf — `null`, wenn es darf. */
export function websiteLuecken(
  p: WebsitePaket,
  aktiveKategorieIds: ReadonlySet<string>,
): { fehlendeSprachen: WebsiteSprache[]; ohneKategorie: boolean } | null {
  const fehlendeSprachen = fehlendeWebsiteSprachen(
    { description: p.description },
    p.translations,
  );
  const ohneKategorie = !(p.categoryIds ?? []).some((id) =>
    aktiveKategorieIds.has(id),
  );
  return fehlendeSprachen.length || ohneKategorie
    ? { fehlendeSprachen, ohneKategorie }
    : null;
}

const hinweis =
  "flex items-start gap-2 rounded-lg px-3 py-2 font-body-sm text-body-sm";
const warnHinweis = `${hinweis} bg-secondary/10 text-secondary dark:text-[#b8ae9e]`;
const infoHinweis = `${hinweis} bg-surface-container-high/60 text-on-surface-variant dark:bg-surface-variant/20 dark:text-secondary-fixed-dim`;
const feldLabel = "mb-1 block font-body-sm text-body-sm font-semibold";

export function PaketWebsiteKarte({
  paket,
  kategorien,
  weinKandidaten = [],
  speichern,
}: {
  paket: WebsitePaket;
  /** Alle Website-Kategorien des Betriebs; archivierte werden nicht angeboten. */
  kategorien: WebsiteKategorie[];
  /** Pakete des Betriebs (ohne dieses, ohne archivierte) für das Feld „Weinbegleitung (Paket)“. */
  weinKandidaten?: WeinKandidat[];
  /** PATCH /api/packages/:id — der Aufrufer schaltet die Website ab, wenn das Paket danach Luecken hat. */
  speichern: (patch: Partial<WebsitePaket>) => Promise<void>;
}) {
  const [reiter, setReiter] = useState<"de" | WebsiteSprache>("de");
  const aktive = kategorien.filter((k) => k.archivedAt == null);
  const aktiveIds = new Set(aktive.map((k) => k.id));
  const luecken = websiteLuecken(paket, aktiveIds);
  const gewaehlt = new Set(paket.categoryIds ?? []);
  const fehlt = (l: WebsiteSprache) =>
    luecken?.fehlendeSprachen.includes(l) ?? false;

  function uebersetzungSpeichern(
    l: WebsiteSprache,
    feld: "name" | "description" | "art",
    wert: string,
  ) {
    const alt = paket.translations?.[l] ?? {};
    if ((alt[feld] ?? "") === wert) return;
    void speichern({
      translations: {
        ...(paket.translations ?? {}),
        [l]: { ...alt, [feld]: wert },
      },
    });
  }

  function kategorieUmschalten(id: string) {
    const neu = gewaehlt.has(id)
      ? [...gewaehlt].filter((x) => x !== id)
      : [...gewaehlt, id];
    void speichern({ categoryIds: neu });
  }

  return (
    <Card className="space-y-4 p-5" data-testid="paket-website-karte">
      <h2 className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        Auf der Website
      </h2>
      <div className="flex items-start gap-3">
        <Toggle
          label="Auf Website zeigen"
          checked={paket.websiteVisible === true}
          disabled={!paket.websiteVisible && luecken != null}
          onChange={(an) => void speichern({ websiteVisible: an })}
        />
        <div className="min-w-0">
          <p className="font-body-sm text-body-sm font-semibold">
            Auf Website zeigen
          </p>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Das Paket erscheint auf den gewählten Seiten, mit dem Preis aus
            diesem Paket.
          </p>
        </div>
      </div>
      {luecken && luecken.fehlendeSprachen.length > 0 ? (
        <p className={warnHinweis} role="note">
          <Icon name="translate" className="text-[18px]" />
          <span>
            {luecken.fehlendeSprachen.map((l) => SPRACH_NAME[l]).join(", ")}{" "}
            {luecken.fehlendeSprachen.length > 1 ? "sind" : "ist"} noch nicht
            gepflegt. Solange das so ist, bleibt der Schalter aus: Die Website
            zeigt jedes Paket in allen vier Sprachen.
          </span>
        </p>
      ) : luecken?.ohneKategorie ? (
        <p className={warnHinweis} role="note">
          <Icon name="sell" className="text-[18px]" />
          <span>
            Wähle mindestens eine Kategorie, sonst weiß die Website nicht, wohin
            das Paket gehört.
          </span>
        </p>
      ) : null}

      <div>
        <span className={feldLabel}>Kategorien (Mehrfachauswahl)</span>
        {aktive.length === 0 ? (
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Noch keine Website-Kategorien. Lege sie unter „Pakete →
            Website-Kategorien“ an.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {aktive.map((k) => {
              const an = gewaehlt.has(k.id);
              return (
                <button
                  key={k.id}
                  type="button"
                  aria-pressed={an}
                  onClick={() => kategorieUmschalten(k.id)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 font-body-sm text-body-sm motion-safe:transition-colors ${
                    an
                      ? "border-primary/40 bg-primary/10 font-semibold text-primary dark:bg-primary/15"
                      : "border-outline-variant text-on-surface-variant dark:text-inverse-on-surface"
                  }`}
                >
                  {an && <Icon name="check" className="text-[15px]" />}
                  {k.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <span className={feldLabel}>Weinbegleitung (Paket)</span>
        <Select
          ariaLabel="Weinbegleitung (Paket)"
          testId="paket-wein-auswahl"
          value={paket.winePairingPackageId ?? ""}
          onValueChange={(v) => void speichern({ winePairingPackageId: v === "" ? null : v })}
          options={[
            { value: "", label: "Automatisch (Name endet auf „mit Weinbegleitung“)" },
            ...weinKandidaten.filter((k) => k.id !== paket.id).map((k) => ({ value: k.id, label: k.name })),
          ]}
        />
        <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          Der Preis dieses Pakets ist der Gesamtpreis mit Weinbegleitung (inklusive Menü) und erscheint auf der Karte als zweiter Preis. Ein verknüpftes Paket wird nur innerhalb derselben Website-Kategorie als eigene Karte ausgeblendet.
        </p>
      </div>

      <div>
        <div
          role="tablist"
          aria-label="Sprache der Übersetzung"
          className="flex gap-5 border-b border-outline-variant/30 dark:border-outline/30"
        >
          {(["de", ...SPRACHEN] as const).map((l) => (
            <button
              key={l}
              type="button"
              role="tab"
              aria-selected={reiter === l}
              onClick={() => setReiter(l)}
              className={`-mb-px inline-flex items-center gap-1 border-b-2 pb-2 font-label-md text-[12.5px] font-semibold ${
                reiter === l
                  ? "border-primary text-primary"
                  : "border-transparent text-on-surface-variant dark:text-secondary-fixed-dim"
              }`}
            >
              {SPRACH_NAME[l]}
              {l !== "de" && (
                <Icon
                  name={fehlt(l) ? "error" : "check_circle"}
                  className={`text-[14px] ${fehlt(l) ? "text-secondary" : "text-success"}`}
                />
              )}
            </button>
          ))}
        </div>
        <div className="pt-3">
          {reiter === "de" ? (
            <div className="space-y-3">
              <p className={infoHinweis}>
                <Icon name="info" className="text-[18px]" />
                <span>
                  Deutsch ist die Hauptsprache. Gepflegt wird oben unter
                  Grunddaten.
                </span>
              </p>
              <ArtFeld
                key="de"
                sprache="de"
                wert={paket.websiteArt ?? ""}
                onSpeichern={(wert) => {
                  if ((paket.websiteArt ?? "") !== wert) void speichern({ websiteArt: wert === "" ? null : wert });
                }}
              />
            </div>
          ) : (
            <UebersetzungsFelder
              key={reiter}
              sprache={reiter}
              paket={paket}
              fehlt={fehlt(reiter)}
              onFeld={(feld, wert) => uebersetzungSpeichern(reiter, feld, wert)}
              onLeeren={() =>
                void speichern({
                  translations: {
                    ...(paket.translations ?? {}),
                    [reiter]: { ...(paket.translations?.[reiter] ?? {}), name: "", description: "" },
                  },
                })
              }
            />
          )}
        </div>
      </div>
    </Card>
  );
}

function UebersetzungsFelder({
  sprache,
  paket,
  fehlt,
  onFeld,
  onLeeren,
}: {
  sprache: WebsiteSprache;
  paket: WebsitePaket;
  fehlt: boolean;
  onFeld: (feld: "name" | "description" | "art", wert: string) => void;
  onLeeren: () => void;
}) {
  const t = paket.translations?.[sprache];
  // Gesteuerte Felder, damit „wie Deutsch“ und „Leeren“ sofort sichtbar sind; gespeichert wird beim Verlassen.
  const [name, setName] = useState(t?.name ?? "");
  const [beschreibung, setBeschreibung] = useState(t?.description ?? "");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={`rounded-full px-2 py-0.5 font-label-md text-[11px] font-semibold ${fehlt ? "bg-secondary/15 text-secondary dark:text-[#b8ae9e]" : "bg-success/15 text-success"}`}
        >
          {fehlt ? "Fehlt noch" : "Gepflegt"}
        </span>
        <span className="flex gap-1.5">
          <Button
            type="button"
            variant="secondary"
            className="!py-1"
            onClick={() => {
              setName(paket.name);
              onFeld("name", paket.name);
            }}
          >
            <Icon name="content_copy" className="text-[16px]" /> wie Deutsch
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="!py-1"
            onClick={() => {
              setName("");
              setBeschreibung("");
              onLeeren();
            }}
          >
            <Icon name="backspace" className="text-[16px]" /> Leeren
          </Button>
        </span>
      </div>
      <label className="block">
        <span className={feldLabel}>Name ({SPRACH_NAME[sprache]})</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onFeld("name", name.trim())}
        />
      </label>
      <label className="block">
        <span className={feldLabel}>Beschreibung ({SPRACH_NAME[sprache]})</span>
        <Textarea
          rows={3}
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
          onBlur={() => onFeld("description", beschreibung.trim())}
        />
      </label>
      <ArtFeld
        sprache={sprache}
        wert={t?.art ?? ""}
        onSpeichern={(wert) => onFeld("art", wert)}
      />
      <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
        Keine Preise in die Übersetzung schreiben. Den Preistext setzt MAESTRO.
      </p>
    </div>
  );
}

const ART_BEISPIEL: Record<"de" | WebsiteSprache, string> = {
  de: "z. B. vegetarisch · 4 Gänge",
  en: "e.g. vegetarian · 4 courses",
  it: "es. vegetariano · 4 portate",
  fr: "p. ex. végétarien · 4 plats",
};

/** „Art auf der Website“ (optional): leer = MAESTRO leitet sie aus Diät-Angaben und Gang-Zahl ab. */
function ArtFeld({
  sprache,
  wert,
  onSpeichern,
}: {
  sprache: "de" | WebsiteSprache;
  wert: string;
  onSpeichern: (wert: string) => void;
}) {
  const [text, setText] = useState(wert);
  return (
    <label className="block">
      <span className={feldLabel}>
        Art auf der Website ({SPRACH_NAME[sprache]}, optional)
      </span>
      <Input
        value={text}
        placeholder={ART_BEISPIEL[sprache]}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onSpeichern(text.trim())}
      />
      <span className="mt-1 block font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
        Leer lassen: MAESTRO setzt sie automatisch aus den Gängen (vegetarisch/vegan nach Katalog, Zahl der Gänge).
      </span>
    </label>
  );
}

/** Vorschau „So erscheint es auf der Website“ in vier Sprachen. */
export function PaketWebsiteVorschau({
  paket,
  kategorien,
}: {
  paket: WebsitePaket;
  kategorien: WebsiteKategorie[];
}) {
  const [sprache, setSprache] = useState<"de" | WebsiteSprache>("de");
  const aktive = kategorien.filter(
    (k) => k.archivedAt == null && (paket.categoryIds ?? []).includes(k.id),
  );
  const text =
    sprache === "de"
      ? { name: paket.name, description: paket.description, art: paket.websiteArt }
      : paket.translations?.[sprache];
  const fehlt =
    sprache !== "de" &&
    fehlendeWebsiteSprachen(
      { description: paket.description },
      paket.translations,
    ).includes(sprache);
  const preis = paketWebsitePreisText(
    { ...paket, minGuests: paket.minGuests ?? null },
    sprache,
  );
  return (
    <Card className="space-y-3 p-5" data-testid="paket-website-vorschau">
      <h2 className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        So erscheint es auf der Website
      </h2>
      <div
        className="inline-flex rounded-lg border border-outline-variant p-0.5"
        role="group"
        aria-label="Sprache der Vorschau"
      >
        {(["de", ...SPRACHEN] as const).map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={sprache === l}
            onClick={() => setSprache(l)}
            className={`rounded-md px-2.5 py-1 font-label-md text-[12px] font-semibold uppercase ${sprache === l ? "bg-primary text-white" : "text-on-surface-variant dark:text-secondary-fixed-dim"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="rounded-[14px] border border-outline-variant/40 p-4 dark:border-outline/40">
        {!paket.websiteVisible ? (
          <div className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[11px] font-semibold dark:bg-white/[0.08]">
              Nicht auf der Website
            </span>
            <p className="mt-2">
              Schalte „Auf Website zeigen“ ein, dann siehst du hier die Karte.
            </p>
          </div>
        ) : fehlt ? (
          <p className="font-body-sm text-body-sm italic text-secondary dark:text-[#b8ae9e]">
            Übersetzung fehlt: Das Paket bliebe in dieser Sprache unsichtbar.
          </p>
        ) : (
          <div className="space-y-1.5">
            <span className="rounded-full bg-success/15 px-2 py-0.5 font-label-md text-[11px] font-semibold text-success">
              Sichtbar in: {aktive.map((k) => k.name).join(", ")}
            </span>
            <h3 className="pt-1 font-body-lg text-body-lg font-semibold">
              {text?.name}
            </h3>
            {text?.art && (
              <p className="font-label-md text-[11px] uppercase tracking-wider text-primary">
                {text.art}
              </p>
            )}
            {text?.description && (
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {text.description}
              </p>
            )}
            {preis.gaeste && (
              <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                {preis.gaeste}
              </p>
            )}
            {preis.preis && (
              <p className="font-body-md font-semibold text-primary">
                {preis.preis}
              </p>
            )}
          </div>
        )}
      </div>
      <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
        Der Preistext wird von MAESTRO gesetzt. Die Website rechnet nie selbst.
      </p>
    </Card>
  );
}
